import { InclusionContent } from "@silversea-ssc/ui-components-react"
import { CruiseGroupCode } from "@silversea-ssc/web-cruise-groups"
import * as E from "fp-ts/Either"
import * as F from "fp-ts/function"
import sanityClient from "../api/sanity-client"
import { Locale } from "../format/localization"
import { SanityContent } from "../getServerSidePropsWithDefaultSanityContent"
import { WebAppConfig } from "../web-app-config"
import { isPresent } from "../checks"
import { buildSanityContentQuery, SanityQueryContent } from "./sanity-queries"
import {
    SanityCruiseData,
    SanityDestination,
    SanityImage,
    SanityInclusionsContent,
    SanityQueryResponse,
    SanityQueryResponseOptional,
    SanityQueryResponseWithOptionalContent,
    toSanityLocale,
} from "./sanity-types"

type Cruise = NonNullable<SanityQueryResponse["cruise"]>
type CruiseData = NonNullable<SanityQueryResponse["cruiseData"]>
type SpecialCruiseData = CruiseData
type SpecialCruiseDestination = SanityQueryResponse["specialCruiseDestination"]
type PageContentCruise = Omit<Cruise, "metadata" | "data"> & {
    destination: SanityDestination
}

type FetchPageContentCruise = {
    cruiseData: Omit<CruiseData, "destination">
    cruise: PageContentCruise
    heroImage: SanityImage
}
export type FetchPageContentMandatory = FetchPageContentCruise & {
    websiteUrls: SanityQueryResponse["websiteUrls"]
    cardPromoBadgesContent: SanityQueryResponse["cardPromoBadgesContent"]
    faresContent: SanityQueryResponse["faresContent"]
    formDisclaimer: SanityQueryResponse["formDisclaimer"]
    termsAndConditionsModal: SanityQueryResponse["termsAndConditionsModal"]
    dynamicFAQ: SanityQueryResponse["dynamicFAQ"]
    inclusionsContent: InclusionContent[]
}
export type FetchPageContentOptional = {
    penaltiesModalContent: SanityQueryResponseOptional["penaltiesModalContent"]
}
export type FetchPageContent = FetchPageContentMandatory & Partial<FetchPageContentOptional>

export type FetchPageContentError = "not-found" | "generic-error"
export type FetchPageContentResponse<K extends SanityQueryContent> = E.Either<FetchPageContentError, SanityContent<K>>

export const fetchPageContent = async <T extends SanityQueryContent>(
    config: WebAppConfig,
    cruiseCode: string,
    locale: Locale,
    includes: readonly T[],
): Promise<FetchPageContentResponse<T>> => {
    const sanityQuery = buildSanityContentQuery(includes)

    const client = sanityClient(config)

    const actualCruiseCode = isDev(config) && cruiseCode === "WC2025" ? "DA250113136" : cruiseCode
    const results = await client.fetch<SanityQueryResponseWithOptionalContent<T>>(sanityQuery, {
        cruiseCode: actualCruiseCode,
        lang: toSanityLocale(locale),
        allowNotVisible: isDev(config) || isStrategicPricingTestCruise(cruiseCode),
    })

    return F.pipe(
        toSanityContent(results, includes),
        E.mapLeft((e) => {
            console.error(`Sanity content not found for ${cruiseCode}.`)
            return e
        }),
    )
}

const toSanityContent = <T extends SanityQueryContent>(
    results: SanityQueryResponseWithOptionalContent<T>,
    _: readonly T[],
): FetchPageContentResponse<T> => {
    const { cruise, cruiseData, specialCruiseDestination, ...common } = results

    if (cruise && cruiseData) {
        const maybeInclusionsContent = maybeConvertSanityInclusionsContent(results, cruiseData.cruiseGroup)

        const content: FetchPageContent = {
            ...common,
            ...(cruiseData._type == "cruiseData"
                ? standardCruisePageContent(cruise, cruiseData)
                : specialCruisePageContent(cruise, cruiseData, specialCruiseDestination)),
            ...maybeInclusionsContent,
        }

        return E.right(content as SanityContent<T>)
    }

    return E.left("not-found")
}

const sanityInclusionsContentToInclusionsContent =
    (cruiseGroup: CruiseGroupCode | undefined) =>
    (sanityInclusionsContent: SanityInclusionsContent): InclusionContent => {
        const { overrideContent, ...content } = sanityInclusionsContent
        const overrideForCruiseGroup =
            cruiseGroup && overrideContent?.find((c) => c.cruiseGroupFilters?.includes(cruiseGroup))
        return {
            ...content,
            infographicTitle: overrideForCruiseGroup?.infographicTitle || content.infographicTitle,
            title: overrideForCruiseGroup?.title || content.title,
            description: overrideForCruiseGroup?.description || content.description,
            footnote: overrideForCruiseGroup?.footnote || content.footnote,
        }
    }

const maybeConvertSanityInclusionsContent = (
    content: SanityQueryResponse,
    cruiseGroup: CruiseGroupCode | undefined,
): { inclusionsContent?: InclusionContent[] } => {
    return content.inclusionsContent
        ? { inclusionsContent: content.inclusionsContent.map(sanityInclusionsContentToInclusionsContent(cruiseGroup)) }
        : {}
}

const standardCruisePageContent = (cruise: Cruise, cruiseData: SanityCruiseData): FetchPageContentCruise => ({
    heroImage: getHeroImage(cruise.cardImage, cruiseData.departurePort.portImage),
    cruise: { ...cruise, destination: cruiseData.destination },
    cruiseData,
})

const specialCruisePageContent = (
    cruise: Cruise,
    cruiseData: SpecialCruiseData,
    cruiseDestination: SpecialCruiseDestination,
): FetchPageContentCruise => ({
    heroImage: getHeroImage(cruise.cardImage, cruiseData.departurePort.portImage),
    cruise: specialPageContentCruise(cruise, cruiseData, cruiseDestination),
    cruiseData,
})

const specialPageContentCruise = (
    cruise: Cruise,
    cruiseData: SpecialCruiseData,
    cruiseDestination: SpecialCruiseDestination,
): PageContentCruise => ({
    ...cruise,
    destination: (() => {
        const dest = specialCruiseDestination(cruiseData, cruiseDestination)
        return {
            destinationWebCode: dest.webCode,
            name: dest.title,
            trackingName: dest.trackingName,
            path: dest.path,
        }
    })(),
})

const specialCruiseDestination = (cruiseData: SpecialCruiseData, cruiseDestination: SpecialCruiseDestination) => {
    if (cruiseData.comboType === "Grand") return { ...cruiseDestination.grandVoyagesPage, webCode: "destination-100" }
    if (cruiseData.comboType === "World") return { ...cruiseDestination.worldCruisesPage, webCode: "destination-101" }
    return { ...cruiseDestination.grandVoyagesPage, webCode: "destination-100" } //TODO is this a good default, it would be better to fail?
}

// Fallback for the low-res cruise card image to the first departure port image
const getHeroImage = (cardImage: SanityImage, portImage: SanityImage): SanityImage => {
    const cardImageSize = cardImage?.asset.metadata?.dimensions?.width
    const lowResCardImage = !cardImageSize || cardImageSize <= 1200
    return lowResCardImage && portImage ? portImage : cardImage
}

function isDev(config: WebAppConfig) {
    return config.stage === "dev"
}

const strategicPricingTestCruises = ["OR300222007", "SL300309016"]
const isStrategicPricingTestCruise = (cruiseCode: string) => strategicPricingTestCruises.includes(cruiseCode)
